import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot
  
} from 'recharts';
import { StockDataPoint, MetricKey } from '../types/StockData';

interface StockChartProps {
  data: StockDataPoint[];
  selectedMetrics: MetricKey[];
  onDataPointClick: (dataPoint: StockDataPoint) => void;
  windowSize: number;
  onWindowSizeChange: (windowSize: number) => void;
}

interface ChartDataPoint {
  timestamp: string;
  time: string;
  price: number;
  metric1: number;
  metric1Name: string;
  metric2?: number;
  metric2Name?: string;
  // Derived display fields (for normalization)
  metric1Display?: number;
  metric2Display?: number;
  // Anomaly flags
  isAnomaly1?: boolean;
  isAnomaly2?: boolean;
}

const StockChart: React.FC<StockChartProps> = ({ 
  data, 
  selectedMetrics, 
  onDataPointClick, 
  windowSize, 
  onWindowSizeChange 
}) => {
  // UI state: normalize metric overlay toggle
  const [normalizeMetric, setNormalizeMetric] = useState<boolean>(false);
  // UI state: anomaly detection method
  type AnomalyMethod = 'zscore' | 'robust';
  const [anomalyMethod, setAnomalyMethod] = useState<AnomalyMethod>('robust');
  // UI state: preset time range (no zoom)
  type RangePreset = 'full' | 'last25' | 'last50' | 'firstHalf' | 'secondHalf';
  const [rangePreset, setRangePreset] = useState<RangePreset>('full');
  const chartData: ChartDataPoint[] = data.map((point): ChartDataPoint => {
    // Ensure timestamp is treated as UTC by adding 'Z' if not present
    const utcTimestamp = point.timestamp.endsWith('Z') ? point.timestamp : point.timestamp + 'Z';
    const utcDate = new Date(utcTimestamp);
    
    // Convert to EST/EDT (UTC-5/UTC-4)
    const estTime = utcDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'America/New_York'
    });

    // Calculate options flow metrics
    const getOptionsMetric = (metricKey: MetricKey): number => {
      const optionsFlow = point.optionFlowInWindow;
      if (!optionsFlow) return 0;
      switch (metricKey) {
        case 'callBidAskRatio':
          return optionsFlow.callBidAskRatio ?? 0;
        case 'putBidAskRatio':
          return optionsFlow.putBidAskRatio ?? 0;
        case 'callPutVolumeRatio':
          return optionsFlow.callPutVolumeRatio ?? 0;
        case 'optionsCallVolume':
          return optionsFlow.call_volume;
        case 'optionsPutVolume':
          return optionsFlow.put_volume;
        case 'optionsTotalVolume':
          return optionsFlow.totalVolume;
        case 'optionsTotalPremium':
          return optionsFlow.totalNetPremium;
        case 'callBidVolume':
          return optionsFlow.call_volume_bid_side;
        case 'callAskVolume':
          return optionsFlow.call_volume_ask_side;
        case 'putBidVolume':
          return optionsFlow.put_volume_bid_side;
        case 'putAskVolume':
          return optionsFlow.put_volume_ask_side;
        case 'ldpm':
          return point.ldpm?.ldpm ?? 0;
        default:
          return point[metricKey] as number;
      }
    };

    const metric1 = selectedMetrics[0] ? getOptionsMetric(selectedMetrics[0]) : 0;
    const metric2 = selectedMetrics[1] ? getOptionsMetric(selectedMetrics[1]) : undefined;

    return {
      timestamp: point.timestamp,
      time: estTime,
      price: point.price,
      metric1,
      metric1Name: selectedMetrics[0] || 'callVol',
      metric2,
      metric2Name: selectedMetrics[1]
    };
  });

  // Prepare display series for metrics: raw or normalized (% change from first non-zero value)
  const metric1RawValues = chartData.map((d: ChartDataPoint) => d.metric1);
  const metric2RawValues = selectedMetrics[1] ? chartData.map((d: ChartDataPoint) => d.metric2 ?? 0) : [];

  const base1 = (() => {
    const nz = metric1RawValues.find((v: number) => v !== 0 && isFinite(v));
    return (nz ?? metric1RawValues[0] ?? 1) || 1;
  })();
  const base2 = (() => {
    const nz = metric2RawValues.find((v: number) => v !== 0 && isFinite(v));
    return (nz ?? metric2RawValues[0] ?? 1) || 1;
  })();

  let chartDisplayData: ChartDataPoint[] = chartData.map((d: ChartDataPoint) => ({
    ...d,
    metric1Display: normalizeMetric ? (((d.metric1 / base1) - 1) * 100) : d.metric1,
    metric2Display: selectedMetrics[1] ? (normalizeMetric ? ((((d.metric2 ?? 0) / base2) - 1) * 100) : d.metric2) : undefined
  }));

  // Compute visible slice based on preset
  const totalPoints = chartDisplayData.length;
  const half = Math.max(1, Math.floor(totalPoints / 2));
  let startIdx = 0;
  let endIdx = Math.max(0, totalPoints - 1);
  switch (rangePreset) {
    case 'last25':
      startIdx = Math.max(0, totalPoints - 25);
      endIdx = Math.max(0, totalPoints - 1);
      break;
    case 'last50':
      startIdx = Math.max(0, totalPoints - 50);
      endIdx = Math.max(0, totalPoints - 1);
      break;
    case 'firstHalf':
      startIdx = 0;
      endIdx = Math.max(0, half - 1);
      break;
    case 'secondHalf':
      startIdx = half;
      endIdx = Math.max(half, totalPoints - 1);
      break;
    case 'full':
    default:
      startIdx = 0;
      endIdx = Math.max(0, totalPoints - 1);
      break;
  }
  const visibleChartData: ChartDataPoint[] = chartDisplayData.slice(startIdx, endIdx + 1);

  // Inline anomaly detection using rolling z-score
  const computeZScoreFlags = (series: number[], window: number, threshold: number): boolean[] => {
    const flags = new Array(series.length).fill(false) as boolean[];
    for (let i = 0; i < series.length; i++) {
      const start = Math.max(0, i - window);
      const slice = series.slice(start, i); // use prior window to avoid leakage
      if (slice.length < Math.min(window, 10)) continue;
      const mean = slice.reduce((s, v) => s + (isFinite(v) ? v : 0), 0) / slice.length;
      const variance = slice.reduce((s, v) => s + Math.pow((isFinite(v) ? v : 0) - mean, 2), 0) / slice.length;
      const std = Math.sqrt(variance);
      if (std === 0 || !isFinite(std)) continue;
      const z = (series[i] - mean) / std;
      if (Math.abs(z) >= threshold) flags[i] = true;
    }
    return flags;
  };

  // Robust heuristic using rolling median & MAD (Median Absolute Deviation)
  const median = (xs: number[]): number => {
    const arr = xs.filter((v) => isFinite(v)).slice().sort((a, b) => a - b);
    if (arr.length === 0) return 0;
    const mid = Math.floor(arr.length / 2);
    return arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
  };

  const mad = (xs: number[], med: number): number => {
    const deviations = xs.map((v) => Math.abs(v - med));
    return median(deviations);
  };

  const computeRobustFlags = (series: number[], window: number, zThresh: number): boolean[] => {
    const flags = new Array(series.length).fill(false) as boolean[];
    for (let i = 0; i < series.length; i++) {
      const start = Math.max(0, i - window);
      const slice = series.slice(start, i);
      if (slice.length < Math.min(window, 10)) continue;
      const med = median(slice);
      const m = mad(slice, med);
      if (!isFinite(m) || m === 0) continue;
      // Convert to a z-like score: 0.6745 * (x - median) / MAD
      const zRobust = 0.6745 * ((series[i] - med) / m);
      if (Math.abs(zRobust) >= zThresh) flags[i] = true;
    }
    return flags;
  };

  const m1Series = chartDisplayData.map((d: ChartDataPoint) => (d.metric1Display as number));
  const m2Series = selectedMetrics[1] ? chartDisplayData.map((d: ChartDataPoint) => (d.metric2Display as number) ?? 0) : [];
  const windowSizeN = 20;
  const zThresh = anomalyMethod === 'robust' ? 3.5 : 2.0;
  const anom1 = anomalyMethod === 'robust'
    ? computeRobustFlags(m1Series, windowSizeN, zThresh)
    : computeZScoreFlags(m1Series, windowSizeN, zThresh);
  const anom2 = selectedMetrics[1]
    ? (anomalyMethod === 'robust'
        ? computeRobustFlags(m2Series, windowSizeN, zThresh)
        : computeZScoreFlags(m2Series, windowSizeN, zThresh))
    : [];

  chartDisplayData = chartDisplayData.map((d: ChartDataPoint, i: number) => ({
    ...d,
    isAnomaly1: anom1[i] || false,
    isAnomaly2: selectedMetrics[1] ? (anom2[i] || false) : undefined
  }));

  // Calculate price domain for better scaling
  const prices = visibleChartData.map(d => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;
  const pricePadding = priceRange * 0.05; // 5% padding
  const priceDomain = [
    Math.max(0, minPrice - pricePadding),
    maxPrice + pricePadding
  ];

  // Calculate metric domains for better scaling
  let metric1Values = visibleChartData.map((d: ChartDataPoint) => (d.metric1Display as number));
  
  // For LDPM, exclude zero values to prevent scale distortion
  if (selectedMetrics[0] === 'ldpm') {
    metric1Values = metric1Values.filter((value: number) => value !== 0);
  }
  
  // Fallback to all values if filtering results in empty array
  if (metric1Values.length === 0) {
    metric1Values = visibleChartData.map((d: ChartDataPoint) => (d.metric1Display as number));
  }
  
  const minMetric1 = Math.min(...metric1Values);
  const maxMetric1 = Math.max(...metric1Values);
  const metric1Range = maxMetric1 - minMetric1;
  const metric1Padding = metric1Range * 0.05; // 5% padding
  const metric1Domain = [
    minMetric1 - metric1Padding,
    maxMetric1 + metric1Padding
  ];

  // Calculate second metric domain if present
  let metric2Domain: [number, number] | undefined;
  if (selectedMetrics[1] && visibleChartData.some((d: ChartDataPoint) => d.metric2Display !== undefined)) {
    let metric2Values = visibleChartData
      .map((d: ChartDataPoint) => d.metric2Display)
      .filter((v: number | undefined) => v !== undefined) as number[];
    
    // For LDPM, exclude zero values to prevent scale distortion
    if (selectedMetrics[1] === 'ldpm') {
      metric2Values = metric2Values.filter((value: number) => value !== 0);
    }
    
    if (metric2Values.length > 0) {
      const minMetric2 = Math.min(...metric2Values);
      const maxMetric2 = Math.max(...metric2Values);
      const metric2Range = maxMetric2 - minMetric2;
      const metric2Padding = metric2Range * 0.05; // 5% padding
      metric2Domain = [
        minMetric2 - metric2Padding,
        maxMetric2 + metric2Padding
      ];
    }
  }

  // Correlation of price returns vs metric1 returns (Pearson)
  const computeReturns = (series: number[]): number[] => {
    const returns: number[] = [];
    for (let i = 1; i < series.length; i++) {
      const prev = series[i - 1];
      const curr = series[i];
      if (!isFinite(prev) || prev === 0 || !isFinite(curr)) continue;
      returns.push((curr - prev) / Math.abs(prev));
    }
    return returns;
  };

  const pearsonCorrelation = (a: number[], b: number[]): number | null => {
    const n = Math.min(a.length, b.length);
    if (n < 3) return null;
    const ax = a.slice(-n);
    const bx = b.slice(-n);
    const mean = (xs: number[]) => xs.reduce((s, v) => s + v, 0) / xs.length;
    const ma = mean(ax);
    const mb = mean(bx);
    let num = 0, da = 0, db = 0;
    for (let i = 0; i < n; i++) {
      const va = ax[i] - ma;
      const vb = bx[i] - mb;
      num += va * vb;
      da += va * va;
      db += vb * vb;
    }
    const denom = Math.sqrt(da * db);
    if (denom === 0) return null;
    return num / denom;
  };

  const priceSeries = visibleChartData.map(d => d.price);
  const metricSeriesForCorr = visibleChartData.map(d => d.metric1);
  const corr = pearsonCorrelation(computeReturns(priceSeries), computeReturns(metricSeriesForCorr));
  const corrText = corr === null ? 'N/A' : (corr >= 0 ? `+${corr.toFixed(2)}` : corr.toFixed(2));
  const corrColorClass = corr === null ? 'text-gray-600 bg-gray-100 border-gray-200' : (corr >= 0 ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200');

  const formatMetricValue = (value: any): string => {
    // Handle non-number values safely
    if (typeof value !== 'number' || isNaN(value)) {
      return 'N/A';
    }
    
    if (Math.abs(value) >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Find the corresponding data point to check for alerts
      const dataIndex = chartData.findIndex(point => point.time === label);
      const originalPoint = dataIndex >= 0 ? data[dataIndex] : null;
      const displayPoint: ChartDataPoint | null = dataIndex >= 0 ? chartDisplayData[dataIndex] : null;
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-xl shadow-2xl backdrop-blur-sm">
          <p className="font-bold text-gray-900 text-base mb-3">{`Time: ${label}`}</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <p className="text-blue-600 font-semibold">
                {`Price: $${payload[0]?.value?.toFixed(2)}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <p className="text-green-600 font-semibold">
                {normalizeMetric
                  ? `${selectedMetrics[0]}: ${Number(payload[1]?.value ?? 0).toFixed(2)}%`
                  : `${selectedMetrics[0]}: ${formatMetricValue(payload[1]?.value)}`}
              </p>
            </div>
            {selectedMetrics[1] && payload[2] && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <p className="text-purple-600 font-semibold">
                  {normalizeMetric
                    ? `${selectedMetrics[1]}: ${Number(payload[2]?.value ?? 0).toFixed(2)}%`
                    : `${selectedMetrics[1]}: ${formatMetricValue(payload[2]?.value)}`}
                </p>
              </div>
            )}
            {displayPoint && (displayPoint.isAnomaly1 || displayPoint.isAnomaly2) && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <p className="text-red-600 text-sm font-semibold">
                  Anomaly detected {displayPoint.isAnomaly1 ? `( ${selectedMetrics[0]} )` : ''} {displayPoint.isAnomaly2 ? `( ${selectedMetrics[1]} )` : ''}
                </p>
              </div>
            )}
          </div>
          
          {/* Show indicators for alerts */}
          {originalPoint?.keyAlerts && originalPoint.keyAlerts.length > 0 && (
            <div className="mt-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm font-bold flex items-center gap-2">
                <span className="text-lg">🔔</span>
                Key Events ({originalPoint.keyAlerts.length})
              </p>
            </div>
          )}
          
          {originalPoint?.flowAlerts && originalPoint.flowAlerts.length > 0 && (
            <div className="mt-3 p-3 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-bold flex items-center gap-2">
                <span className="text-lg">⚡</span>
                Flow Alerts ({originalPoint.flowAlerts.length})
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const handleChartClick = (event: any) => {
    if (event && event.activeLabel) {
      // Find the data point that matches the clicked time
      const clickedTime = event.activeLabel;
      const clickedDataPoint = data.find(point => {
        const utcTimestamp = point.timestamp.endsWith('Z') ? point.timestamp : point.timestamp + 'Z';
        const utcDate = new Date(utcTimestamp);
        const estTime = utcDate.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          timeZone: 'America/New_York'
        });
        return estTime === clickedTime;
      });
      
      if (clickedDataPoint) {
        onDataPointClick(clickedDataPoint);
      }
    }
  };



  const windowOptions = [
    { value: 1, label: '1 minute' },
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' }
  ];

  return (
    <div className="w-full h-[600px] bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-xl border border-gray-100 p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 tracking-tight">
          Stock Price vs {selectedMetrics.join(' & ')}
        </h3>
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">Window:</label>
          <select
            value={windowSize}
            onChange={(e) => onWindowSizeChange(parseInt(e.target.value))}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:border-gray-400 transition-colors"
          >
            {windowOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* Preset time range chips */}
          <div className="hidden md:flex items-center gap-1 ml-2">
            {([
              { key: 'full', label: 'Full' },
              { key: 'last50', label: 'Last 50' },
              { key: 'last25', label: 'Last 25' },
              { key: 'firstHalf', label: 'First half' },
              { key: 'secondHalf', label: 'Second half' },
            ] as { key: RangePreset; label: string }[]).map(btn => (
              <button
                key={btn.key}
                onClick={() => setRangePreset(btn.key)}
                className={`px-2 py-1 text-xs rounded-full border transition-colors ${rangePreset === btn.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                title={`Show ${btn.label.toLowerCase()}`}
              >
                {btn.label}
              </button>
            ))}
          </div>
          {/* Anomaly method selector */}
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 ml-2">
            Anomaly:
            <select
              value={anomalyMethod}
              onChange={(e) => setAnomalyMethod(e.target.value as AnomalyMethod)}
              className="px-2 py-1 text-xs font-medium border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="robust">Robust</option>
              <option value="zscore">Z-Score</option>
            </select>
          </label>
          {/* Normalize toggle */}
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 ml-2">
            <input
              type="checkbox"
              checked={normalizeMetric}
              onChange={(e) => setNormalizeMetric(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Normalize metric
          </label>
          {/* Correlation badge */}
          <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-lg border ${corrColorClass}`} title="Correlation of price vs metric returns">
            Corr: {corrText}
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart 
          data={visibleChartData} 
          margin={{ top: 10, right: 40, left: 30, bottom: 10 }} 
          onClick={handleChartClick}
        >
          <defs>
            <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="metric1Gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="metric2Gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.1}/>
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 2" stroke="#e5e7eb" strokeOpacity={0.5} />
          <XAxis 
            dataKey="time" 
            stroke="#6b7280"
            fontSize={13}
            fontWeight={500}
            tickLine={false}
            axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
          />
          <YAxis 
            yAxisId="price"
            orientation="left"
            stroke="#3b82f6"
            fontSize={13}
            fontWeight={600}
            tickFormatter={(value) => `$${value.toFixed(2)}`}
            domain={priceDomain}
            tickLine={false}
            axisLine={{ stroke: '#3b82f6', strokeWidth: 2 }}
          />
          <YAxis 
            yAxisId="metric1"
            orientation="right"
            stroke="#10b981"
            fontSize={13}
            fontWeight={600}
            tickFormatter={(v) => normalizeMetric ? `${(v as number).toFixed(2)}%` : formatMetricValue(v)}
            domain={metric1Domain}
            tickLine={false}
            axisLine={{ stroke: '#10b981', strokeWidth: 2 }}
          />
          {selectedMetrics[1] && metric2Domain && (
            <YAxis 
              yAxisId="metric2"
              orientation="right"
              stroke="#8b5cf6"
              fontSize={13}
              fontWeight={600}
              tickFormatter={(v) => normalizeMetric ? `${(v as number).toFixed(2)}%` : formatMetricValue(v)}
              domain={metric2Domain}
              tickLine={false}
              axisLine={{ stroke: '#8b5cf6', strokeWidth: 2 }}
              dx={60}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="price"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#ffffff' }}
            name="Price ($)"
            fill="url(#priceGradient)"
          />
          <Line
            yAxisId="metric1"
            type="monotone"
            dataKey="metric1Display"
            stroke="#10b981"
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#ffffff' }}
            name={normalizeMetric ? `${selectedMetrics[0]} (% from start)` : `${selectedMetrics[0]}`}
            fill="url(#metric1Gradient)"
          />
          {selectedMetrics[1] && (
            <Line
              yAxisId="metric2"
              type="monotone"
              dataKey="metric2Display"
              stroke="#8b5cf6"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2, fill: '#ffffff' }}
              name={normalizeMetric ? `${selectedMetrics[1]} (% from start)` : `${selectedMetrics[1]}`}
              fill="url(#metric2Gradient)"
            />
          )}

          {/* Inline anomaly markers for metric1 */}
          {visibleChartData.map((d: ChartDataPoint, i: number) => (
            d.isAnomaly1 ? (
              <ReferenceDot
                key={`anom1-${i}`}
                x={d.time}
                y={d.metric1Display as number}
                yAxisId="metric1"
                r={4}
                fill="#ef4444"
                stroke="#ffffff"
                strokeWidth={1}
              />
            ) : null
          ))}
          {/* Inline anomaly markers for metric2 */}
          {selectedMetrics[1] && visibleChartData.map((d: ChartDataPoint, i: number) => (
            d.isAnomaly2 ? (
              <ReferenceDot
                key={`anom2-${i}`}
                x={d.time}
                y={(d.metric2Display as number)}
                yAxisId="metric2"
                r={4}
                fill="#f59e0b"
                stroke="#ffffff"
                strokeWidth={1}
              />
            ) : null
          ))}
          
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StockChart;
